import { useEffect, useState } from "react";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";

const LS_KEY = "parentia_reminder_permission_requested";
const LS_LAST = "parentia_reminder_last_shown";

export default function DailyReminder({ challenges }) {
  const [supported] = useState(typeof window !== "undefined" && "Notification" in window);
  const [status, setStatus] = useState(supported ? Notification.permission : "unsupported");

  const openChallenges = challenges.filter(
    (c) => c.completed_days.length < c.duration_days
  );

  useEffect(() => {
    if (!supported || status !== "granted" || openChallenges.length === 0) return;
    const today = new Date().toDateString();
    const last = localStorage.getItem(LS_LAST);
    if (last === today) return;
    // Fire a soft in-tab reminder now (once per day per session)
    const timer = setTimeout(() => {
      const first = openChallenges[0];
      new Notification("Kynoria · Rappel doux 💛", {
        body: `Prends 2 minutes pour cocher aujourd'hui : « ${first.title} »`,
        icon: "/favicon.ico",
      });
      localStorage.setItem(LS_LAST, today);
    }, 1500);
    return () => clearTimeout(timer);
  }, [status, openChallenges, supported]);

  const askPermission = async () => {
    if (!supported) {
      toast.info("Notifications non supportées sur ce navigateur");
      return;
    }
    const res = await Notification.requestPermission();
    setStatus(res);
    localStorage.setItem(LS_KEY, "1");
    if (res === "granted") {
      toast.success("Rappels activés 🔔");
      new Notification("Kynoria", {
        body: "Tu recevras un rappel doux chaque jour pour tes défis.",
      });
    } else if (res === "denied") {
      toast.error("Rappels bloqués. Autorise-les dans les réglages du navigateur.");
    }
  };

  if (openChallenges.length === 0) return null;

  return (
    <div
      data-testid="daily-reminder"
      className="soft-card p-4 flex items-center gap-3 !bg-[#FFF4DC] border-[#F4D06F]"
    >
      <div className="h-10 w-10 rounded-2xl bg-white/70 grid place-items-center shrink-0">
        {status === "granted" ? (
          <BellRing className="h-5 w-5 text-[#E8899F]" />
        ) : (
          <Bell className="h-5 w-5 text-[#16233D]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#16233D]">
          {openChallenges.length} défi{openChallenges.length > 1 ? "s" : ""} en cours
        </p>
        <p className="text-xs text-[#44506B]">
          {status === "granted"
            ? "Un rappel doux te sera envoyé chaque jour."
            : "Active les rappels doux pour ne rien oublier."}
        </p>
      </div>
      {status !== "granted" && (
        <button
          data-testid="enable-reminders-btn"
          onClick={askPermission}
          className="brand-btn rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap"
        >
          Activer
        </button>
      )}
    </div>
  );
}
